import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client';
import { Badge, ApiResponse, PaginatedResponse } from '@tinyrewards/shared';
import type { TimedQuestion } from './youtube';

export function getBadges(params?: {
    page?: number;
    pageSize?: number;
    public?: boolean;
    search?: string;
    sort?: 'createddate' | 'name' | 'status' | 'verified';
    order?: 'asc' | 'desc';
    visibility?: 'all' | 'public' | 'private';
    activeFilter?: 'all' | 'active' | 'inactive';
    badgeType?: string;
}): Promise<PaginatedResponse<Badge>> {
    const qs = new URLSearchParams();
    if (params?.page)         qs.set('page', String(params.page));
    if (params?.pageSize)     qs.set('pageSize', String(params.pageSize));
    if (params?.public)       qs.set('public', 'true');
    if (params?.search)       qs.set('search', params.search);
    if (params?.sort)         qs.set('sort', params.sort);
    if (params?.order)        qs.set('order', params.order);
    if (params?.visibility)   qs.set('visibility', params.visibility);
    if (params?.activeFilter) qs.set('activeFilter', params.activeFilter);
    if (params?.badgeType)    qs.set('badgeType', params.badgeType);
    const q = qs.toString();
    return apiGet<Badge[]>(`/api/badges${q ? '?' + q : ''}`) as Promise<PaginatedResponse<Badge>>;
}

export function getBadge(id: string): Promise<ApiResponse<Badge>> {
    return apiGet<Badge>(`/api/badges/${id}`);
}

export function createBadge(data: {
    badgeName: string;
    badgeDescription: string;
    badgeCriteria?: string;
    isPublic: boolean;
    tags?: string;
    issuerID?: string;
    badgeType?: string;
    badgeObjective?: object;
}): Promise<ApiResponse<Badge>> {
    return apiPost<Badge>('/api/badges', data);
}

export async function uploadBadgeImage(badgeID: string, file: File): Promise<ApiResponse<{ badgeImage: string }>> {
    const form = new FormData();
    form.append('image', file);
    const res = await fetch(`/api/badges/${badgeID}/image`, {
        method: 'POST',
        credentials: 'include',
        body: form,
    });
    return res.json();
}

export type BadgeUpdatePayload = Partial<{
    badgeName: string;
    badgeDescription: string;
    badgeCriteria: string;
    isPublic: boolean;
    tags: string;
    isActive: boolean;
    pageUrl: string;
    pageTasks: Array<{ id: string; text: string; required: boolean }>;
}>;

export function updateBadge(id: string, data: BadgeUpdatePayload): Promise<ApiResponse<Badge>> {
    return apiPut<Badge>(`/api/badges/${id}`, data);
}

export function deleteBadge(id: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/badges/${id}`);
}

export function setActiveBadge(id: string, active: boolean): Promise<ApiResponse<void>> {
    return apiPatch<void>(`/api/badges/${id}/${active ? 'activate' : 'deactivate'}`);
}

export function saveTimedQuestions(
    badgeID: string,
    data: {
        questions:     TimedQuestion[];
        videoDuration?: string | number | null;
        passingScore?:  number;
        name?:          string;
    },
): Promise<ApiResponse<{
    badgeID:       string;
    pageID:        string;
    questionCount: number;
    assessmentID:  string;
}>> {
    return apiPost<{
        badgeID:       string;
        pageID:        string;
        questionCount: number;
        assessmentID:  string;
    }>(`/api/badges/${badgeID}/timed-questions`, data);
}

export function getAssignedBadges(): Promise<ApiResponse<Badge[]>> {
    return apiGet<Badge[]>('/api/badges/assigned');
}

export function getFavourites(): Promise<ApiResponse<Badge[]>> {
    return apiGet<Badge[]>('/api/badges/favourites');
}

export function addFavourite(badgeID: string): Promise<ApiResponse<void>> {
    return apiPost<void>(`/api/badges/${badgeID}/favourite`, {});
}

export function removeFavourite(badgeID: string): Promise<ApiResponse<void>> {
    return apiDelete<void>(`/api/badges/${badgeID}/favourite`);
}

export function earnBadge(badgeID: string): Promise<ApiResponse<{ alreadyEarned?: boolean }>> {
    return apiPost<{ alreadyEarned?: boolean }>(`/api/badges/${badgeID}/earn`, {});
}
